import type { Dispatch, SetStateAction } from 'react'
import { Form } from '@epages/react-components'
import { createElement, useCallback, useEffect, useRef, useState } from 'react'
import { fromJS } from 'immutable'
import { kebabCase, partial } from 'lodash-es'
import { useDispatch, useSelector } from 'react-redux'
import cc from 'classcat'

import { formatHref } from '../templateComponents/Workspace/utils/link-sanitizer'
import { getDefaultValue } from './FooterSettings'
import {
  previewShopLogo,
  previewThemeSettings,
  previewUserSettings,
  setShopLogo,
  setThemeSettings,
  setUserSettings,
} from '../../store/actions'
import { useTranslate } from '../../utils/translate'
import Button from '../formComponents/Button'
import ButtonRow from '../formComponents/ButtonRow'
import HeaderAnnouncementSettings from './HeaderAnnouncementSettings'
import HeaderTypeSettings from './HeaderTypeSettings'
import HeaderUspSettings from './HeaderUspSettings'
import MenuTypeSettings from './MenuTypeSettings'
import SettingsLayer from '../templateComponents/Workspace/SettingsLayer'
import ShopLogoSettings from './ShopLogoSettings'

// Beware: Key is also used as translation key and content access key
const tabs = {
  themeHeader: HeaderTypeSettings,
  headerMenu: MenuTypeSettings,
  shopLogo: ShopLogoSettings,
  headerUsps: HeaderUspSettings,
  headerAnnouncement: HeaderAnnouncementSettings,
} as Record<string, any>

export type HeaderSettingsTab = keyof typeof tabs

export type SwitchTabFunction = Dispatch<SetStateAction<keyof typeof tabs>>

export const convertSizingFromPxToPercent = (sizingPx: number): number => Math.round((sizingPx - 20) / 1.8)
const convertSizingFromPercentToPx = (sizingPercent: number): number => Math.floor(sizingPercent * 1.8) + 20

type Props = {
  active: boolean | HeaderSettingsTab
  onToggle: ({ submit }?: { submit: boolean }) => void
}

export default function HeaderSettings({ active, onToggle }: Readonly<Props>) {
  const [activeTab, setActiveTab] = useState(typeof active === 'string' ? active : 'shopLogo')
  const t = useTranslate('interface')

  const dispatch = useDispatch()

  const themeSettings = useSelector<State, ImmutableMap>((state) => state.getIn(['shop', 'themeSettings']))
  const defaultThemeSettings = useSelector<State, ImmutableMap>((state) => state.get('defaultThemeSettings'))
  const themeId = useSelector<State, string>((state) => state.getIn(['shop', 'theme', 'id']))

  const shopLocale = useSelector<State, string>((state) => state.getIn(['shop', 'locale']))
  const taxCountry = useSelector<State, string>((state) => state.getIn(['tax', 'country']))
  const shopResellerName = useSelector<State, string>((state) => state.get('resellerName'))
  const userSettings = useSelector<State, ImmutableMap>((state) =>
    state.getIn(['shop', 'userSettings']).delete('googleAnalytics'),
  )

  const logoAdjustmentThemeSettings: ImmutableMap =
    themeSettings?.get('logoAdjustments') || defaultThemeSettings.get('logoAdjustments')

  const shopLogoSettings = useSelector<State, ImmutableMap>((state) =>
    fromJS({
      shouldHideLogo: state.getIn(['shop', 'shouldHideLogo']) || Boolean(!state.getIn(['shop', 'logoUrl'])),
      imageData: {
        src: state.getIn(['shop', 'logoUrl']),
        width: state.getIn(['shop', 'logoWidth']),
        height: state.getIn(['shop', 'logoHeight']),
      },
      themeSettings: {
        logoAdjustments: logoAdjustmentThemeSettings,
      },
    }),
  )

  const themeHeaderSettings: ImmutableMap = themeSettings?.get('themeHeader') || defaultThemeSettings.get('themeHeader')

  const headerAnnouncementSettings: ImmutableMap = fromJS({
    announcementHtml: userSettings.getIn(['headerAnnouncement', 'announcementHtml']),
    active: userSettings.getIn(['headerAnnouncement', 'active']),
    backgroundColor:
      themeSettings?.getIn(['headerAnnouncement', 'backgroundColor']) ||
      defaultThemeSettings.getIn(['headerAnnouncement', 'backgroundColor']),
  })

  const isBusy = useSelector<State, boolean>((state) => state.getIn(['view', 'busy']))

  const initialShopLogoSettings = useRef(shopLogoSettings)

  // Resets any unsaved local changes (changes that are not affecting data that is also used in other bottom
  // bar setting layers). Shared bottom bar settings changes are reset in the parent `BottomBar` component.
  const resetUnsavedLocalChanges = useCallback(() => {
    const imageData = initialShopLogoSettings.current.get('imageData')
    const shouldHideLogo = initialShopLogoSettings.current.get('shouldHideLogo')

    dispatch(
      previewShopLogo({
        logoUrl: imageData.get('src'),
        logoWidth: imageData.get('width'),
        logoHeight: imageData.get('height'),
        shouldHideLogo,
      }),
    )
  }, [dispatch])

  // Reset any unsaved local changes on close.
  useEffect(() => {
    return () => {
      if (active) resetUnsavedLocalChanges()
    }
  }, [active, resetUnsavedLocalChanges])

  // Activate given tab in case of deep linking to it.
  useEffect(() => {
    if (typeof active === 'string') setActiveTab(active)
  }, [active])

  const scopedT = (subKey: string, ...args: any[]) => t('components.bottomBar.headerSettings.' + subKey, ...args)

  const params = { shopLocale, shopResellerName, taxCountry }
  // Collect tab value default except for headerMenu tab default so that it's not submitted with userSettings
  const tabValueDefaults = Object.entries(tabs)
    .map<ImmutableMap>(partial(getDefaultValue, params))
    .filter((tabValueDefault) => !tabValueDefault.has('headerMenu'))

  const mboBaseUrl = useSelector<State, string>((state) => state.getIn(['shop', 'mboBaseUrl']))

  const onSubmit = async (data: ImmutableMap) => {
    const userSettingsData = data
      .delete('shopLogo')
      .delete('themeHeader')
      .deleteIn(['headerAnnouncement', 'backgroundColor'])

    const sanitizedUserSettingsData = userSettingsData.updateIn(['headerUsps'], (usps: ImmutableList) => {
      return usps.map((uspItem: ImmutableMap) => {
        const link = uspItem.get('link')
        if (link) return uspItem.set('link', formatHref(link))
        return uspItem
      })
    })
    await dispatch(setUserSettings(sanitizedUserSettingsData.toJS()))

    const imageData = data.getIn(['shopLogo', 'imageData'])
    await dispatch(
      setShopLogo({
        logoUrl: imageData.get('src'),
        logoWidth: imageData.get('width'),
        logoHeight: imageData.get('height'),
        shouldHideLogo: data.getIn(['shopLogo', 'shouldHideLogo']),
      }),
    )

    const themeSettingsData = themeSettings
      .set('logoAdjustments', data.getIn(['shopLogo', 'themeSettings', 'logoAdjustments']))
      .set('themeHeader', data.get('themeHeader'))
      .setIn(['headerAnnouncement', 'backgroundColor'], data.getIn(['headerAnnouncement', 'backgroundColor']))

    await dispatch(setThemeSettings(themeId, themeSettingsData))

    // send head settings to MBO
    window.parent?.postMessage(
      {
        type: 'HEADER_SETTINGS',
        payload: {
          logoUrl: imageData.get('src'),
          shouldHideLogo: data.getIn(['shopLogo', 'shouldHideLogo']),
        },
      },
      mboBaseUrl,
    )

    // Auto-checks the "Upload your logo" to-do in the BYD cockpit's dashboard checklist and setup guide
    if (!shopLogoSettings.equals(initialShopLogoSettings.current))
      window.parent?.postMessage(
        {
          type: 'CHECKLIST',
          payload: {
            websiteOnly: 'designSettings.logoUpload',
            regularShop: 'designSettings.logoUpload',
          },
        },
        mboBaseUrl,
      )

    initialShopLogoSettings.current = shopLogoSettings

    onToggle({ submit: true })
  }

  const onCancel = () => {
    const newThemeSettings = themeSettings.set(
      'logoAdjustments',
      initialShopLogoSettings.current.getIn(['themeSettings', 'logoAdjustments']),
    )

    resetUnsavedLocalChanges()
    dispatch(previewThemeSettings(newThemeSettings))

    onToggle()
  }

  const onDataChange = (formData: ImmutableMap) => {
    const data = normalize(formData)
    const userSettingsPreview = data
      .delete('shopLogo')
      .delete('themeHeader')
      .deleteIn(['headerAnnouncement', 'backgroundColor'])

    requestAnimationFrame(() => {
      if (activeTab === 'headerUsps') {
        dispatch(previewUserSettings(userSettingsPreview))
      }

      if (activeTab === 'headerAnnouncement') {
        const themeSettingsPreview = themeSettings.setIn(
          ['headerAnnouncement', 'backgroundColor'],
          formData.getIn(['headerAnnouncement', 'backgroundColor']),
        )

        dispatch(previewThemeSettings(themeSettingsPreview))
        dispatch(previewUserSettings(userSettingsPreview))
      }

      if (activeTab === 'themeHeader' || activeTab === 'headerMenu') {
        const themeSettingsPreview = themeSettings.set('themeHeader', formData.get('themeHeader'))

        dispatch(previewThemeSettings(themeSettingsPreview))
      }

      if (activeTab === 'shopLogo') {
        const shopLogoBasicSettings = data.getIn(['shopLogo']).delete('themeSettings').delete('themeHeader')

        if (!shopLogoSettings.delete('themeSettings').equals(shopLogoBasicSettings)) {
          const imageData = shopLogoBasicSettings.get('imageData')

          dispatch(
            previewShopLogo({
              logoUrl: imageData.get('src'),
              logoWidth: imageData.get('width'),
              logoHeight: imageData.get('height'),
              shouldHideLogo: shopLogoBasicSettings.get('shouldHideLogo'),
            }),
          )
        } else {
          const themeSettingsPreview = themeSettings.set(
            'logoAdjustments',
            data.getIn(['shopLogo', 'themeSettings', 'logoAdjustments']),
          )

          dispatch(previewThemeSettings(themeSettingsPreview))
        }
      }
    })

    // Make sure not to return any data in order to prevent 'smart' data handling
    // https://github.com/ePages-de/react-components/blob/v0.4.3/src/form/Form.jsx#L142-L143
    return null
  }

  const validate = (data: ImmutableMap, isOnSubmit = true) => {
    const errors: any = {}

    const shouldHideLogo = data.getIn(['shopLogo', 'shouldHideLogo'])
    const imageData = data.getIn(['shopLogo', 'imageData'])

    if (!(imageData.get('src') || shouldHideLogo)) {
      errors.shopLogo = errors.shopLogo || {}
      errors.shopLogo.imageData = { src: scopedT('errors.noImage') }
    }

    if (isOnSubmit) {
      return Object.keys(errors).length > 0 ? fromJS(errors) : undefined
    }
  }

  const prepare = (data: ImmutableMap) => {
    const selector = ['shopLogo', 'themeSettings', 'logoAdjustments', 'sizing']
    return (
      data
        .mergeDeep(...tabValueDefaults, data)
        .updateIn(selector, (v) => convertSizingFromPxToPercent(v))
        // Apply preparation actions for the header types.
        .updateIn(['themeHeader'], HeaderTypeSettings.prepare)
    )
  }

  const normalize = (data: ImmutableMap) => {
    const selector = ['shopLogo', 'themeSettings', 'logoAdjustments', 'sizing']
    return data.updateIn(selector, (v) => convertSizingFromPercentToPx(v))
  }

  const formValues = userSettings
    .set('shopLogo', shopLogoSettings)
    .set('themeHeader', themeHeaderSettings)
    .set('headerAnnouncement', headerAnnouncementSettings)

  return (
    <>
      <button
        className={cc(['ep-bottom-bar-button', 'ep-bottom-bar-button-header-settings', { checked: active }])}
        title={scopedT('title')}
        onClick={() => onToggle()}
      />
      <SettingsLayer
        className={cc(['ep-settingslayer-header-settings', `${kebabCase(activeTab)}-active`])}
        onEscapeKeyDown={onCancel}
        open={!!active}
        side="top"
        align="center"
      >
        {({ renderLayout }) => (
          <Form
            name="headerSettings"
            value={formValues}
            prepare={prepare}
            normalize={normalize}
            onChange={onDataChange}
            onSubmit={onSubmit}
            validate={validate}
          >
            {renderLayout({
              title: scopedT(`${activeTab}.title`),
              tabs: Object.keys(tabs).map((tab) => ({
                name: tab,
                title: scopedT(`${tab}.title`),
                active: activeTab === tab,
                onClick: (name: typeof activeTab) => setActiveTab(name),
              })),
              body: createElement(tabs[activeTab], {
                switchTab: setActiveTab,
              }),
              footer: (
                <ButtonRow>
                  <Button disabled={isBusy} type="button" variant="cancel" onClick={onCancel}>
                    {scopedT('cancelButton.label')}
                  </Button>
                  <Button disabled={isBusy} type="submit">
                    {scopedT('applyButton.label')}
                  </Button>
                </ButtonRow>
              ),
            })}
          </Form>
        )}
      </SettingsLayer>
    </>
  )
}
